import clsx from "clsx";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ArrowDownIcon, CheckIcon, GlobeIcon } from "../Icons";
import { Typo } from "../Typo";

export type LanguageSelectOption = {
  label: string;
  value: string;
};

type LanguageSelectProps = {
  options: LanguageSelectOption[];
  value: string;
  onChange: (option: LanguageSelectOption) => void;
  className?: string;
  dropUp?: boolean;
  "data-testid"?: string;
  /** When true, renders dropdown with high z-index to appear above modals */
  modal?: boolean;
};

export const LanguageSelect: React.FC<LanguageSelectProps> = ({
  options,
  value,
  onChange,
  className,
  dropUp = false,
  "data-testid": testId,
  modal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  // Track when position has been calculated to avoid flash at (0,0) on first render
  const [positionReady, setPositionReady] = useState(false);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  // Update dropdown position when modal mode is active
  useLayoutEffect(() => {
    if (!modal || !isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportHeight = window.innerHeight;
      const dropdownHeight = dropdownRef.current?.offsetHeight ?? 200;
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const shouldDropUp = dropUp || spaceBelow < dropdownHeight;

      setDropdownPosition({
        top: shouldDropUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 200),
      });
    };

    // Find all scrollable parents to attach scroll listeners
    const getScrollableParents = (element: HTMLElement): HTMLElement[] => {
      const scrollableParents: HTMLElement[] = [];
      let parent = element.parentElement;
      while (parent) {
        const style = getComputedStyle(parent);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        if (
          overflowY === "auto" ||
          overflowY === "scroll" ||
          overflowX === "auto" ||
          overflowX === "scroll"
        ) {
          scrollableParents.push(parent);
        }
        parent = parent.parentElement;
      }
      return scrollableParents;
    };

    const scrollableParents = getScrollableParents(buttonRef.current);

    // Close dropdown when scrolling inside a scrollable parent (e.g. modal)
    // to prevent the dropdown from floating outside modal boundaries
    const closeOnScroll = () => setIsOpen(false);

    // Reset position ready state when dropdown opens
    setPositionReady(false);

    // Use requestAnimationFrame to ensure dropdown is rendered before measuring
    const frameId = requestAnimationFrame(() => {
      updatePosition();
      // Recalculate after dropdown has rendered to get accurate height
      requestAnimationFrame(() => {
        updatePosition();
        setPositionReady(true);
      });
    });

    // Close dropdown when scrolling inside parent containers
    for (const parent of scrollableParents) {
      parent.addEventListener("scroll", closeOnScroll);
    }
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(frameId);
      for (const parent of scrollableParents) {
        parent.removeEventListener("scroll", closeOnScroll);
      }
      window.removeEventListener("resize", updatePosition);
    };
  }, [modal, isOpen, dropUp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideContainer =
        containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown =
        !dropdownRef.current || !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset position ready state when dropdown closes to prevent flash at old position on reopen
  useEffect(() => {
    if (!isOpen) {
      setPositionReady(false);
    }
  }, [isOpen]);

  const handleSelect = (option: LanguageSelectOption) => {
    onChange(option);
    setIsOpen(false);
  };

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className={clsx(
        "min-w-[200px] rounded-lg border border-stroke-primary bg-surface-primary p-2 shadow-dropdown",
        modal ? "fixed z-floating" : "absolute left-0 z-50",
        !modal && (dropUp ? "bottom-full mb-1" : "top-full mt-1"),
      )}
      style={
        modal
          ? {
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              minWidth: dropdownPosition.width,
              // Hide dropdown until position is calculated to avoid flash at (0,0)
              visibility: positionReady ? "visible" : "hidden",
            }
          : undefined
      }
      data-testid={testId ? `${testId}-dropdown` : undefined}
    >
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option)}
            className={clsx(
              "flex w-full items-center justify-between rounded-md p-2 hover:bg-surface-tonal-default",
              isSelected && "bg-state-selected",
            )}
          >
            <Typo
              type="bodySM"
              textColor={isSelected ? "text-text-selected" : undefined}
            >
              {option.label}
            </Typo>
            {isSelected && (
              <CheckIcon className="h-4 w-4 [&_path]:stroke-stroke-selected" />
            )}
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div className={clsx("relative", className)} ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="text-text-brand flex items-center gap-1 rounded-full bg-surface-primary px-2 py-1"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <GlobeIcon className="h-3 w-3" />
        <Typo type="bodyXS" textColor="text-text-brand">
          {selectedLabel}
        </Typo>
        <ArrowDownIcon className="h-2 w-2 [&_path]:stroke-text-brand-default" />
      </button>
      {modal ? createPortal(dropdownContent, document.body) : dropdownContent}
    </div>
  );
};
